/**
 * Template de prise de besoin : Plan de communication d'incident v1
 * Aligné sur NIST CSF 2.0, ITIL 4, ISO 27001 et COBIT 2019
 * Plan détaillé pour la communication lors d'incidents
 */

import type { IntakeTemplate } from '../templateTypes';

export const templatePlanCommunicationIncidentV1: IntakeTemplate = {
  id: 'plan-communication-incident-v1',
  name: 'Prise de besoin Plan de communication d\'incident v1',
  description:
    'Questionnaire complet pour élaborer un plan de communication d\'incident conforme NIST CSF 2.0, ITIL 4, ISO 27001 et COBIT 2019. Définit les canaux, templates, audiences, messages et processus de communication interne et externe.',
  documentTypeCode: 'INCIDENT_COMMUNICATION_PLAN',
  version: '1.0.0',
  phases: [
    {
      id: 'phase1',
      title: 'Contexte & Audiences',
      description: 'Informations sur l\'entreprise et identification des audiences',
      icon: 'Building2',
      questions: [
        {
          id: 'entreprise',
          label: 'Entreprise',
          type: 'text',
          placeholder: 'Nom de l\'entreprise',
          required: true,
          helpText: 'Identifiant principal pour le plan de communication.',
        },
        {
          id: 'audiences_internes',
          label: 'Quelles sont les audiences internes à informer ?',
          type: 'multiselect',
          options: [
            'Direction',
            'Gestionnaires',
            'Employés',
            'Équipe TI',
            'Équipe sécurité',
            'RH',
            'Juridique',
            'Autre',
          ],
          required: true,
          helpText: 'NIST CSF 2.0 (RS.CO): Audiences internes pour communication efficace.',
        },
        {
          id: 'audiences_externes',
          label: 'Quelles sont les audiences externes à informer ?',
          type: 'multiselect',
          options: [
            'Clients',
            'Fournisseurs',
            'Partenaires',
            'Autorités réglementaires',
            'Médias',
            'Public',
            'Autre',
          ],
          helpText: 'NIST CSF 2.0 (RS.CO): Audiences externes pour communication transparente.',
        },
        {
          id: 'ton_document_plan',
          label: 'Quel ton souhaitez-vous pour ce plan ?',
          type: 'select',
          options: [
            'Formel et professionnel (ton corporatif)',
            'Transparent et rassurant (ton communication de crise)',
            'Mixte (équilibre entre formel et transparent)',
          ],
          helpText: 'Le ton influence la perception et la confiance des audiences.',
        },
      ],
    },
    {
      id: 'phase2',
      title: 'Canaux & Méthodes de Communication',
      description: 'Canaux et méthodes de communication',
      icon: 'MessageSquare',
      questions: [
        {
          id: 'canaux_internes',
          label: 'Quels canaux sont utilisés pour la communication interne ?',
          type: 'multiselect',
          options: [
            'Courriel',
            'Teams / Slack',
            'Intranet',
            'Réunions',
            'Appels téléphoniques',
            'SMS / Notifications',
            'Autre',
          ],
          required: true,
          helpText: 'NIST CSF 2.0 (RS.CO): Canaux internes pour communication rapide.',
        },
        {
          id: 'canaux_externes',
          label: 'Quels canaux sont utilisés pour la communication externe ?',
          type: 'multiselect',
          options: [
            'Courriel clients',
            'Site web',
            'Réseaux sociaux',
            'Médias',
            'Communiqués de presse',
            'Appels téléphoniques',
            'Autre',
          ],
          helpText: 'NIST CSF 2.0 (RS.CO): Canaux externes pour communication transparente.',
        },
        {
          id: 'frequence_communication',
          label: 'Quelle est la fréquence de communication lors d\'un incident ?',
          type: 'select',
          options: [
            'Immédiate puis horaire',
            'Immédiate puis toutes les 2 heures',
            'Immédiate puis quotidienne',
            'Selon évolution de l\'incident',
            'Autre',
          ],
          helpText: 'NIST CSF 2.0 (RS.CO): Fréquence pour maintenir les audiences informées.',
        },
        {
          id: 'responsable_communication',
          label: 'Qui est responsable de la communication lors d\'incidents ?',
          type: 'text',
          placeholder: 'Ex: Responsable communication, Relations publiques, Gestionnaire d\'incidents...',
          required: true,
          helpText: 'NIST CSF 2.0 (RS.CO): Responsable pour coordination de la communication.',
        },
      ],
    },
    {
      id: 'phase3',
      title: 'Templates & Messages',
      description: 'Templates et messages de communication',
      icon: 'FileText',
      questions: [
        {
          id: 'types_templates',
          label: 'Quels types de templates sont nécessaires ?',
          type: 'multiselect',
          options: [
            'Notification interne initiale',
            'Mise à jour interne',
            'Notification clients',
            'Communication publique',
            'Communication réglementaire',
            'Communication médias',
            'Autre',
          ],
          required: true,
          helpText: 'NIST CSF 2.0 (RS.CO): Templates pour communication cohérente.',
        },
        {
          id: 'contenu_messages',
          label: 'Quel contenu doit être inclus dans les messages ?',
          type: 'multiselect',
          options: [
            'Description de l\'incident',
            'Impact sur les services',
            'Mesures prises',
            'Délai de résolution estimé',
            'Actions requises des utilisateurs',
            'Contact pour questions',
            'Autre',
          ],
          helpText: 'NIST CSF 2.0 (RS.CO): Contenu essentiel pour communication complète.',
        },
        {
          id: 'langue_communication',
          label: 'Dans quelle(s) langue(s) communiquez-vous ?',
          type: 'multiselect',
          options: ['Français', 'Anglais', 'Français et Anglais', 'Autre'],
          helpText: 'Adaptation linguistique pour audiences diverses.',
        },
        {
          id: 'approbation_messages',
          label: 'Qui doit approuver les messages avant envoi ?',
          type: 'select',
          options: [
            'Responsable communication uniquement',
            'Responsable communication + Direction',
            'Comité de communication',
            'Selon type d\'audience',
            'Autre',
          ],
          helpText: 'NIST CSF 2.0 (RS.CO): Processus d\'approbation pour messages cohérents.',
        },
      ],
    },
    {
      id: 'phase4',
      title: 'Processus & Gestion de Crise',
      description: 'Processus de communication et gestion de crise',
      icon: 'AlertTriangle',
      questions: [
        {
          id: 'processus_communication',
          label: 'Quel est le processus de communication lors d\'un incident ?',
          type: 'textarea',
          placeholder: 'Décrivez les étapes: Détection → Évaluation → Préparation message → Approbation → Envoi → Suivi...',
          helpText: 'NIST CSF 2.0 (RS.CO): Processus structuré pour communication efficace.',
        },
        {
          id: 'gestion_medias',
          label: 'Comment gérez-vous la communication avec les médias ?',
          type: 'select',
          options: [
            'Pas de communication médias',
            'Communication proactive',
            'Communication réactive uniquement',
            'Porte-parole désigné',
            'Autre',
          ],
          helpText: 'Gestion médias pour protéger la réputation.',
        },
        {
          id: 'communication_reglementaire',
          label: 'Comment gérez-vous la communication réglementaire (Loi 25, RGPD) ?',
          type: 'textarea',
          placeholder: 'Décrivez le processus: Évaluation → Notification CAI → Avis personnes concernées...',
          helpText: 'Loi 25 / RGPD: Obligations légales pour communication réglementaire.',
        },
        {
          id: 'revision_plan',
          label: 'À quelle fréquence souhaitez-vous réviser ce plan ?',
          type: 'select',
          options: ['Annuelle', 'Semestrielle', 'Trimestrielle', 'Après chaque incident majeur', 'À déterminer'],
          helpText: 'NIST CSF 2.0: Révision régulière essentielle pour maintenir l\'efficacité.',
        },
      ],
    },
  ],
};





